import React, { createContext, useContext, useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { trustCenterApi, organizationsApi } from '../services/api';

export const REQUEST_TYPES = [
  { id: 'documents', label: 'DOCUMENTS' },
  { id: 'assessment', label: 'ASSESSMENT' },
  { id: 'query', label: 'QUERY' },
];

const INITIAL_FORM = {
  fullName: '',
  email: '',
  organization: '',
  newOrg: '',
  requestType: 'query',
  queryAbout: '',
  message: '',
};

const GuestContext = createContext(null);

export const GuestProvider = ({ children }) => {
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);

  useEffect(() => {
    organizationsApi
      .getAll(1, 500)
      .then((res) => setOrganizations(res?.data ?? []))
      .catch(() => setOrganizations([]))
      .finally(() => setOrgsLoading(false));
  }, []);

  const openRequestModal = () => {
    setSubmitSuccess(false);
    setSubmitError(null);
    setIsRequestModalOpen(true);
  };

  const closeRequestModal = () => setIsRequestModalOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (selected) => {
    if (!selected) return;
    const arr = Array.from(selected).filter((f) => f.size <= 10 * 1024 * 1024);
    setFiles((prev) => [...prev, ...arr]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const setRequestType = (typeId) => {
    setForm((prev) => ({ ...prev, requestType: typeId }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await trustCenterApi.submitRequest(form, files);
      setSubmitSuccess(true);
      setForm(INITIAL_FORM);
      setFiles([]);
      setIsRequestModalOpen(false);
      setSubmitSuccess(false);
      await Swal.fire({
        icon: 'success',
        title: 'Request Submitted!',
        text: 'Your request has been sent successfully. We will get back to you shortly.',
        confirmButtonColor: '#0077b5',
        confirmButtonText: 'OK',
      });
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GuestContext.Provider
      value={{
        // Modal
        isRequestModalOpen,
        openRequestModal,
        closeRequestModal,
        // Form
        form,
        handleChange,
        setRequestType,
        // Files
        files,
        isDragging,
        setIsDragging,
        handleFileSelect,
        handleDrop,
        removeFile,
        // Submit
        isSubmitting,
        submitSuccess,
        submitError,
        handleSubmit,
        // Organizations
        organizations,
        orgsLoading,
      }}
    >
      {children}
    </GuestContext.Provider>
  );
};

export const useGuest = () => {
  const ctx = useContext(GuestContext);
  if (!ctx) throw new Error('useGuest must be used inside GuestProvider');
  return ctx;
};
